import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import ScreenshotCarousel from '../components/ScreenshotCarousel';

const FeaturesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-saverly-primary/10 to-saverly-deep/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-saverly-dark font-poppins mb-6">
            Powerful Features for Smarter
            <span className="block text-saverly-primary">Meal Planning</span>
          </h1>
          <p className="text-xl text-saverly-gray font-inter max-w-3xl mx-auto">
            Everything you need to save time, money, and stress in your meal planning journey.
          </p>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Feature 1 */}
            <div className="space-y-6">
              <div className="w-16 h-16 bg-saverly-primary rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-saverly-dark font-poppins">AI-Powered Meal Planning</h2>
              <p className="text-lg text-saverly-gray font-inter leading-relaxed">
                Generate complete meal plans in seconds. Our AI creates full recipes with main dishes, sides, vegetables, and seasonings—all tailored to your dietary preferences and family size.
              </p>
              <ul className="space-y-3">
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    18 dietary preferences (Vegan, Keto, Gluten-Free, etc.)
                  </li>
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete meals with sides & vegetables (10-20 ingredients)
                  </li>
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Cuisine variety & spice level preferences
                  </li>
              </ul>
            </div>
            <div className="flex justify-center items-center">
              <div className="w-96">
                <ScreenshotCarousel
                  images={[
                    { src: '/screenshots/meal-plan-1.png', caption: 'Family size and dietary restrictions' },
                    { src: '/screenshots/meal-plan-2.png', caption: 'Cuisine, cook time, and cooking methods' },
                    { src: '/screenshots/meal-plan-3.png', caption: 'Browse sales items to generate a meal from' },
                    { src: '/screenshots/meal-plan-4.png', caption: 'Five AI-generated recipes based on your selection' },
                    { src: '/screenshots/meal-plan-5.png', caption: 'Step-by-step cooking instructions' },
                    { src: '/screenshots/meal-plan-6.png', caption: 'Complete ingredients list with add to cart' }
                  ]}
                  alt="AI Meal Planning"
                />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Feature 2 - Order reversed */}
            <div className="flex justify-center items-center lg:order-first">
              <div className="w-96">
                <ScreenshotCarousel
                  images={[
                    { src: '/screenshots/product-matching-1.png', caption: 'Ingredients automatically matched to Kroger products' },
                    { src: '/screenshots/product-matching-2.png', caption: 'Swap to premium alternatives with one tap' }
                  ]}
                  alt="Smart Product Matching"
                />
              </div>
            </div>
            <div className="space-y-6">
              <div className="w-16 h-16 bg-saverly-accent rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-saverly-dark font-poppins">Smart Product Matching</h2>
              <p className="text-lg text-saverly-gray font-inter leading-relaxed">
                Every ingredient from your meal plan is intelligently matched to real products from Kroger family stores. Our sophisticated scoring algorithm considers category, freshness, brand preference, and price to find the perfect items.
              </p>
              <ul className="space-y-3">
                                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Category-based filtering (Produce, Dairy, Meat, etc.)
                  </li>
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Intelligent scoring system
                  </li>
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Stock level & fulfillment info
                  </li>
              </ul>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Feature 3 */}
            <div className="space-y-6">
              <div className="w-16 h-16 bg-saverly-deep rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-saverly-dark font-poppins">Detailed Product Information</h2>
              <p className="text-lg text-saverly-gray font-inter leading-relaxed">
                View comprehensive details for every product: nutrition facts, ingredients, stock status, sale dates, aisle location, and more. Make informed decisions with complete information.
              </p>
              <ul className="space-y-3">
                                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Full nutrition information & ratings
                  </li>
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Aisle locations for faster shopping
                  </li>
                  <li className="flex items-center text-saverly-gray">
                    <svg className="w-5 h-5 text-saverly-primary mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    SNAP eligible & organic badges
                  </li>
              </ul>
            </div>
            <div className="flex justify-center items-center">
              <div className="w-96">
                <ScreenshotCarousel
                  images={[
                    { src: '/screenshots/product-detail-1.png', caption: 'Complete nutrition facts with Kroger OptUP® rating, SNAP/Organic badges, and fulfillment options' }
                  ]}
                  alt="Detailed Product Information"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-saverly-dark font-poppins mb-4">
              Even More Ways to Save
            </h2>
            <p className="text-xl text-saverly-gray font-inter">
              Additional features that make Saverly your complete meal planning solution.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-saverly-primary rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-saverly-dark font-poppins">Live Sales Browsing</h3>
              <p className="text-saverly-gray font-inter">Browse real sales from Kroger family stores by category with product images, prices, and discount percentages.</p>
            </div>

            {/* Feature 2 */}
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-saverly-accent rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-saverly-dark font-poppins">Shopping Lists</h3>
              <p className="text-saverly-gray font-inter">Create and manage multiple shopping lists with aisle information and price tracking.</p>
            </div>

            {/* Feature 3 */}
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-saverly-deep rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-saverly-dark font-poppins">Meal Wizard</h3>
              <p className="text-saverly-gray font-inter">Start a meal plan from any product with our interactive wizard that generates 5 creative options.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-saverly-primary to-saverly-accent text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-poppins mb-6">
            Ready to Experience All These Features?
          </h2>
          <p className="text-xl text-white/90 font-inter mb-8">
            Start your free trial today and see how much time and money you can save.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="w-full sm:w-auto bg-white text-saverly-primary hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transition-all duration-200 transform hover:scale-105">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" className="w-full sm:w-auto bg-saverly-dark text-white hover:bg-saverly-dark/90 px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-200">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage; 